if (!customElements.get('ss-mobile-buy-bar')) {
  customElements.define('ss-mobile-buy-bar', class extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.form = document.getElementById(this.dataset.formId);
      this.sourceButton = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
      if (!this.productInfo || !this.form || !this.sourceButton || !('IntersectionObserver' in window)) return;

      this.submitButton = this.querySelector('[data-submit]');
      this.submitLabel = this.querySelector('[data-submit-label]');
      this.price = this.querySelector('[data-price]');
      this.optionsButton = this.querySelector('[data-options]');
      this.variantTitle = this.querySelector('[data-variant-title]');
      this.error = this.querySelector('[data-error]');
      this.media = window.matchMedia('(max-width: 749px)');
      this.abortController = new AbortController();
      const listenerOptions = { signal: this.abortController.signal };

      this.submitButton.addEventListener('click', () => this.submit(), listenerOptions);
      this.optionsButton?.addEventListener('click', () => this.showOptions(), listenerOptions);
      this.productInfo.addEventListener('change', () => this.scheduleSync(), listenerOptions);
      this.media.addEventListener('change', () => this.sync(), listenerOptions);

      this.observer = new MutationObserver((mutations) => {
        if (mutations.some(({ target }) => !this.contains(target))) this.scheduleSync();
      });
      this.observer.observe(this.productInfo, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'hidden', 'class', 'value']
      });

      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.buttonPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.updateVisibility();
      });
      this.intersectionObserver.observe(this.sourceButton);

      this.spacer = document.createElement('div');
      this.spacer.className = 'ss-mobile-buy-bar-spacer';
      this.spacer.setAttribute('aria-hidden', 'true');
      document.body.append(this.spacer);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          const height = Math.ceil(this.getBoundingClientRect().height);
          if (height) this.spacer.style.setProperty('--ss-mobile-buy-bar-height', `${height}px`);
        });
        this.resizeObserver.observe(this);
      }
      this.sync();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      this.spacer?.remove();
    }

    scheduleSync() {
      if (this.syncFrame) return;
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        if (this.isConnected) this.sync();
      });
    }

    sync() {
      const source = this.sourceButton;
      const variantInput = this.form.querySelector('[name="id"]');
      const disabled = source.disabled || !variantInput?.value || variantInput.disabled;
      const busy = source.getAttribute('aria-disabled') === 'true' || source.classList.contains('loading');
      this.submitButton.disabled = disabled;
      this.submitButton.setAttribute('aria-disabled', String(disabled || busy));
      this.submitButton.classList.toggle('loading', busy && !disabled);
      const sourceLabel = source.querySelector('span');
      if (sourceLabel) this.submitLabel.textContent = sourceLabel.textContent;
      this.submitButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy || disabled);
      if (source.hasAttribute('aria-haspopup')) {
        this.submitButton.setAttribute('aria-haspopup', source.getAttribute('aria-haspopup'));
      }

      const sourcePrice = document.getElementById(`price-${this.dataset.sectionId}`);
      this.price.hidden = !sourcePrice || sourcePrice.hidden || sourcePrice.classList.contains('hidden');
      if (sourcePrice && this.lastPrice !== sourcePrice.innerHTML) {
        this.price.innerHTML = sourcePrice.innerHTML;
        this.price.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        this.lastPrice = sourcePrice.innerHTML;
      }

      const picker = this.productInfo.querySelector('variant-selects');
      if (this.optionsButton) this.optionsButton.hidden = !picker;
      if (this.variantTitle && picker) {
        try {
          const variant = JSON.parse(picker.querySelector('[data-selected-variant]')?.textContent || 'null');
          if (variant?.title) this.variantTitle.textContent = variant.title;
        } catch {}
      }

      const sourceError = this.form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      const errorText = sourceError && !sourceError.hidden
        ? sourceError.querySelector('.product-form__error-message')?.textContent.trim()
        : '';
      this.error.textContent = errorText || '';
      this.error.hidden = !errorText;
      this.updateVisibility();
    }

    updateVisibility() {
      const visible = this.media.matches && this.buttonPassed && this.sourceButton.getClientRects().length > 0;
      this.hidden = !visible;
    }

    submit() {
      const source = this.sourceButton;
      const variantInput = this.form.querySelector('[name="id"]');
      if (source.disabled || source.getAttribute('aria-disabled') === 'true' || source.classList.contains('loading') || !variantInput?.value || variantInput.disabled) return;
      if (!this.form.reportValidity()) return;
      this.form.requestSubmit(source);
      this.scheduleSync();
    }

    showOptions() {
      const picker = this.productInfo.querySelector('variant-selects');
      if (!picker) return;
      picker.scrollIntoView({ block: 'center', behavior: 'instant' });
      const control = picker.querySelector('select, input:checked, input:not([disabled]), button');
      control?.focus({ preventScroll: true });
    }
  });
}
